import React, { RefObject, useEffect } from 'react'
import { View, Dimensions, ScrollView } from 'react-native'
import PropTypes from 'prop-types'
import { ConfiguratorV2 } from './configurator-v2'
import { REACT_NATIVE_API } from '../constants'
import { isPortrait } from '../orientation'
import { Node } from '../types/node'
import { Group } from '../types/group'
import { QuestionType } from '../types/question'

type ConfiguratorV2CTNProps = {
  /** The partner specific SKU. */
  customSKU: string | null,
  /** The catalog to use. Examples are ('partner', 'fisher', 'fisheredu') */
  catalog: string,
  /** The partner code. */
  partner: string,
  /** The partners display title. */
  displayName: string,
  /** Show the mobile product display */
  showProductDisplay: boolean,
  /** Callback to set the sku for the image viewer on the left on desktop. */
  onSetSKU: Function,
  /** The primary color. */
  accentColor: string,
  /** The color text title. */
  color: string | null,
  /** Reference to the main scroll view for auto scroll feature. */
  scrollViewRef: RefObject<ScrollView>,
  /** The height of the chair image on top. Used for autoscroll on portrait. */
  headerHeight: number | null,
  /** opens the quote cart. */
  openQuoteCart: Function,
  /** The tree data for the partner. */
  treeData: object,
  /** Key sku in assoc table form. */
  keySKUTable: object,
  /** THe hostname of the partner website for the product page button. */
  host: string,
  extraAttributes: object,
  /** Initial sku to render options for. */
  initSKU: string | null,
  /** function to set initSKU. Takes in a sku string */
  setInitSKU: Function,
}



export const ConfiguratorV2CTN = ({ customSKU, catalog, partner, displayName, showProductDisplay, onSetSKU, accentColor, color, scrollViewRef, headerHeight, openQuoteCart, treeData, keySKUTable, host, extraAttributes, initSKU, setInitSKU }: ConfiguratorV2CTNProps) => {
  const [sku, setSKU] = React.useState<string | null>(null)
  const [baseSKU, setBaseSKU] = React.useState<string | null>(null)
  const [description, setDescription] = React.useState<string | null>(null)
  const [options, setOptions] = React.useState<Array<string> | null>(null)
  const [path, setPath] = React.useState<Array<Node>>([])
  const [optionsSelected, setOptionsSelected] = React.useState<Map<string, string>>(new Map<string, string>())
  const [yLocations, setYLocations] = React.useState<Map<number, number>>(new Map<number, number>())
  const [optionGroups, setOptionGroups] = React.useState<Array<any>>([])
  const [answerClicked, setAnswerClicked] = React.useState<string | null>(null)
  const [selectedRootAnswer, setSelectedRootAnswer] = React.useState<string | null>(null)
  const [scroll, setScroll] = React.useState<number | null>(null)
  const [secondaryHeightSwitchClicked, setSecondaryHeightSwitchClicked] = React.useState(false)
  const [offsetIndex, setOffsetIndex] = React.useState<number | null>(null)
  const [render, setRender] = React.useState(false)
  const [leafNode, setLeafNode] = React.useState<any>(null)

  const rootNode = treeData.find((node: Node) => node.parent === 'root')
  const rootID = rootNode.id

  React.useEffect(() => {

    if (selectedRootAnswer && !leafNode) {

      const newPath = initPath(treeData, selectedRootAnswer!, path.map((node: Node) => node.name))
      if (newPath) {
        const node = newPath.find((node: Node) => node.type === 'leaf')
        const defaultSKU = node?.attributes.defaultSKU
        const baseSKU = node!.name
        const localOptions = defaultSKU!.replace(baseSKU + '-', '').split('-')
        const groupOptions = fetchOptions(baseSKU, localOptions)

        if (groupOptions) {
          // setBaseSKU(baseSKU)
          setOptions(localOptions)
          // Set the initial values of the options so that the options don't flicker.

          const table = new Map<string, string>()

          for (var groupIndex = 0; groupIndex < groupOptions.length; groupIndex += 1) {
            const optionGroup = groupOptions[groupIndex]

            optionGroup.selected = 0

            const optionCodes = optionGroup.group.map((group: Group) => group.option)
            const tableKey = optionCodes.reduce((a: string, b: string) => a + b)

            table.set(tableKey, optionCodes[optionGroup.selected])
          }

          for (var i = 0; i < localOptions.length; i += 1) {
            const option = localOptions[i]
            const options = groupOptions.find((optGroup: any) => optGroup.group.map((g: any) => g.option).includes(option))
            const key = options?.group?.map((opt: any) => opt.option).reduce((a: string, b: string) => a + b) ?? ''
            localOptions[i] = table.get(key) ?? localOptions[i]
          }

          setOptionsSelected(table)
          setOptionGroups(groupOptions)
          setPath(newPath)
          setSKU(getSKU(baseSKU, localOptions))
          setBaseSKU(baseSKU)
          onSetSKU(getSKU(baseSKU, localOptions))
          setOffsetIndex(1)
          setRender(true)
        }
      }
    }
  }, [selectedRootAnswer])

  React.useEffect(() => {
    if (answerClicked) {
      setSecondaryHeightSwitchClicked(false)
      const cache = path?.map((node: Node) => node.name)
      const depth = calculateNodeDepth(answerClicked ?? '')
      const subPath = initSubPath(treeData, answerClicked ?? '', cache)
      const newPath = path.slice(0, depth - 2).concat(subPath)
      const node = newPath.find(node => node.type === 'leaf')
      const defaultSKU = node?.attributes.defaultSKU
      const baseSKU = node?.name ?? 'VDHCH-RG'
      const localOptions = defaultSKU!.replace(baseSKU + '-', '').split('-')
      const groupOptions = fetchOptions(baseSKU, localOptions)
      if (groupOptions) {
        setOptionGroups(groupOptions)
        // Iterate the options
        // If optionGroups for that option is in the optionsSelected table
        // then use the value from that table.
        for (var i = 0; i < localOptions.length; i += 1) {
          const option = localOptions[i]
          const options = groupOptions.find((optGroup: any) => optGroup.group.map((g: any) => g.option).includes(option))
          const key = options?.group?.map((opt: any) => opt.option).reduce((a: string, b: string) => a + b)
          localOptions[i] = optionsSelected.get(key!) ?? localOptions[i]
        }

        setOptions(localOptions)

        setPath(newPath)
        setSKU(getSKU(baseSKU, localOptions))
        setBaseSKU(baseSKU)
        onSetSKU(getSKU(baseSKU, localOptions))

        if (!secondaryHeightSwitchClicked) {
          const index = getQuestions()?.findIndex((q: Node) => q.parent === treeData.find((node: Node) => node.id === answerClicked).parent)
          setOffsetIndex(index)
          setRender(true)
        }

      }

    }
  }, [answerClicked])

  React.useEffect(() => {
    if (sku) {
      (async () => {
        const desc = await fetchDescription(sku)
        setDescription(desc)
      })()

    }
  }, [sku])

  React.useEffect(() => {
    if (scroll) {
      // scrollViewRef.current.scrollTo({y: scroll, animated: true})
      scrollConfigurator(isPortrait() && headerHeight !== null ? (scroll ?? 0) * 0.96 + (headerHeight ?? 0) : (scroll ?? 0))
    }
  }, [scroll])

  React.useEffect(() => {
    if (yLocations && offsetIndex !== null && yLocations.get(offsetIndex) !== undefined) {
      setScroll(yLocations.get(offsetIndex)!)

    }
  }, [yLocations, offsetIndex])

  React.useEffect(() => {
    setRender(false)
    if (render && yLocations.size > 0 && offsetIndex !== null) {
      setRender(false)
      // scrollViewRef.current.scrollTo({y: yLocations[offsetIndex], animated: true})
      scrollConfigurator(isPortrait() && headerHeight !== null ? yLocations.get(offsetIndex)! * 0.96 + headerHeight : yLocations.get(offsetIndex)!)
    }
  }, [render])

  React.useEffect(() => {
    // This useEffect prefills the configurator if an initalSKU is provided by the App
    if (leafNode) {

      let arr: Array<any> = [];
      const reversePath = (currentNode: any) => {
        if (currentNode.parent !== "root") {
          arr.unshift(currentNode)
          reversePath(treeData.find(node => node.id === currentNode.parent))
        } else {
          arr.unshift(currentNode)
          return
        }
      }
      reversePath(leafNode);
      const FIRST_QUESTION_INDEX = 1;
      setSelectedRootAnswer(arr[FIRST_QUESTION_INDEX].id)
      const localOptions = leafNode.attributes.defaultSKU!.replace(leafNode.name + '-', '').split('-')
      const optionsTable = fetchOptions(leafNode.name, localOptions)
      if (optionsTable) {
        setOptions(localOptions)
        // Set the initial values of the options so that the options don't flicker.

        const table = new Map<string, string>()

        for (var groupIndex = 0; groupIndex < optionsTable.length; groupIndex += 1) {
          const optionGroup = optionsTable[groupIndex]
          let optionSelected = 0;

          for(let optionIndex = 0; optionIndex < optionsTable[groupIndex].group.length; optionIndex++) {
            if (localOptions[groupIndex] === optionsTable[groupIndex].group[optionIndex].option) {
              optionSelected = optionIndex
              optionsTable[groupIndex].selected = optionIndex
            }
          }

          optionGroup.selected = optionSelected

          const optionCodes = optionGroup.group.map((group: Group) => group.option)
          const tableKey = optionCodes.reduce((a: string, b: string) => a + b)

          table.set(tableKey, optionCodes[optionGroup.selected])
        }

        for (var i = 0; i < localOptions.length; i += 1) {
          const option = localOptions[i]
          const options = optionsTable.find((optGroup: any) => optGroup.group.map((g: any) => g.option).includes(option))
          const key = options?.group?.map((opt: any) => opt.option).reduce((a: string, b: string) => a + b) ?? ''
          localOptions[i] = table.get(key) ?? localOptions[i]
        }

        setOptionsSelected(table)
        setOptionGroups(optionsTable)
        setPath(arr)
        setSKU(getSKU(leafNode.name, localOptions))
        setBaseSKU(leafNode.name)
        onSetSKU(getSKU(leafNode.name, localOptions))
        setOffsetIndex(1)
        setRender(true)
      }
      onAnswerChange(leafNode.id)
      setLeafNode(null);
    }
  }, [leafNode, initSKU])

  const scrollConfigurator = (offset: number) => {
    if (offset) {
      scrollViewRef?.current?.scrollTo({ y: offset, animated: true })
    }
  }

  const onReset = () => {
    setSKU(null)
    setBaseSKU(null)
    setDescription(null)
    setOptions(null)
    setPath([])
    setOptionsSelected(new Map<string, string>())
    setYLocations(new Map<number, number>())
    setOptionGroups([])
    setAnswerClicked(null)
    setSelectedRootAnswer(null)
    setScroll(null)
    setSecondaryHeightSwitchClicked(false)
    scrollViewRef?.current?.scrollTo({ y: 0, animated: true })
  }

  const fetchDescription = async (sku: string): Promise<string | null> => {
    try {
      const baseURL = REACT_NATIVE_API
      const res = await fetch(baseURL + '/description.php?part=' + sku + '&catalog=' + catalog)
      const text = await res.json()
      return text
    } catch (error) {
      console.log({ error })
      return null
    }
  }

  const getSKU = (baseSKU: string, options: Array<string>) => {
    let defaultSKU = baseSKU

    for (const option of options) {
      defaultSKU = defaultSKU + '-' + option
    }

    return defaultSKU
  }

  const fetchOptions = (id: string, optionsArg: Array<string>): Array<any> | null => {
    try {
      let tmpOptionGroups = []

      const res = keySKUTable.find(row => row.get('SKU') === id)

      let tiltGroup = []
      let headRestGroup = []
      let armGroup = []
      let controlGroup = []
      let footringGroup = []
      let feetGroup = []
      let upholsteryGroup = []
      let skinGroup = []
      let colorGroup = []

      for (const r of res ?? []) {
        const option = r[0]
        const value = r[1]
        if (value === '1') {
          switch (option) {
            case 'T0':
              tiltGroup.push({ option: option, label: 'No Tilt' })
              break
            case 'T1':
              tiltGroup.push({ option: option, label: 'Seat Tilt' })
              break
            case 'C1':
              controlGroup.push({ option: option, label: 'Standard Tilt' })
              break
            case 'C2':
              controlGroup.push({ option: option, label: 'Knee Bend' })
              break
            case 'A0':
              armGroup.push({ option: option, label: 'None' })
              break
            case 'A1':
              armGroup.push({ option: option, label: 'Adjustable' })
              break
            case 'A2':
              armGroup.push({ option: 'A1', label: '2-Way' })
              break
            case 'A3':
              armGroup.push({ option: 'A2', label: '3D' })
              break
            case 'NF':
              footringGroup.push({ option: option, label: 'None' })
              break
            case 'CF20R':
              footringGroup.push({ option: 'CF', label: 'Chrome' })
              break
            case 'CF20O':
              footringGroup.push({ option: 'CF', label: 'Chrome' })
              break
            case 'CF18':
              footringGroup.push({ option: 'CF', label: 'Chrome' })
              break
            case 'BF':
              footringGroup.push({ option: option, label: 'Black' })
              break
            case 'RC':
              feetGroup.push({ option: option, label: 'Casters' })
              break
            case 'RG':
              feetGroup.push({ option: option, label: 'Glides' })
              break
            case 'RG1':
              feetGroup.push({ option: 'RG', label: '1" Glides' })
              break
            case 'CC':
              feetGroup.push({ option: option, label: 'Chrome Casters' })
              break
            case 'EC':
              feetGroup.push({ option: option, label: 'ESD Casters' })
              break
            case 'EG':
              feetGroup.push({ option: option, label: 'ESD Glides' })
              break
            case 'HC':
              feetGroup.push({ option: option, label: 'Hard Casters' })
              break
            case 'HCC':
              feetGroup.push({ option: option, label: 'Hard Chrome' })
              break
            case 'RBC':
              feetGroup.push({ option: option, label: 'Casters' })
              break
            case 'NBC':
              feetGroup.push({ option: option, label: 'Casters' })
              break
            case 'H0':
              headRestGroup.push({ option: option, label: 'None' })
              break
            case 'H1':
              headRestGroup.push({ option: option, label: 'Headrest' })
              break
            case 'Fabric':
              upholsteryGroup.push({
                option: 'F41',
                label: 'Fabric Burgandy',
              })
              upholsteryGroup.push({ option: 'F42', label: 'Fabric Black' })
              upholsteryGroup.push({ option: 'F43', label: 'Fabric Blue' })
              upholsteryGroup.push({ option: 'F44', label: 'Fabric Gray' })
              upholsteryGroup.push({ option: 'F45', label: 'Fabric Navy' })
              break
            case 'Patriot':
              upholsteryGroup.push({ option: '8540', label: 'Black' })
              upholsteryGroup.push({ option: '8546', label: 'Blue' })
              upholsteryGroup.push({ option: '8569', label: 'Burgundy' })
              upholsteryGroup.push({ option: '8605', label: 'Charcoal' })
              upholsteryGroup.push({ option: '8584', label: 'Taupe' })
              upholsteryGroup.push({ option: '8582', label: 'Imperial' })
              upholsteryGroup.push({ option: '8567', label: 'Dove' })
              upholsteryGroup.push({ option: '8501', label: 'Adobe' })
              upholsteryGroup.push({ option: '8613', label: 'Orange Kist' })
              break
            case 'SilverTex':
              if (catalog === 'fisher' || catalog === 'fisheredu') {
                upholsteryGroup.push({ option: '8822', label: 'Storm' })
                upholsteryGroup.push({ option: '8809', label: 'Taupe' })
                upholsteryGroup.push({ option: '8823', label: 'Carbon' })
                upholsteryGroup.push({ option: '8801', label: 'Marine' })
                upholsteryGroup.push({ option: '8815', label: 'Borscht' })
                upholsteryGroup.push({ option: '8840', label: 'Sterling' })
              } else {
                upholsteryGroup.push({ option: '8822', label: 'Storm' })
                upholsteryGroup.push({ option: '8809', label: 'Taupe' })
                upholsteryGroup.push({ option: '8823', label: 'Carbon' })
                upholsteryGroup.push({ option: '8801', label: 'Marine' })
                upholsteryGroup.push({ option: '8815', label: 'Borscht' })
                upholsteryGroup.push({ option: '8840', label: 'Sterling' })
              }
              break
            case 'Whisper':
              break
            case 'Fisher Whisper':
              if (catalog === 'fisheredu') {
                upholsteryGroup.push({ option: '2134', label: 'Patina' })
                upholsteryGroup.push({ option: '2129', label: 'Forest' })
                upholsteryGroup.push({ option: '2160', label: 'Sassafras' })
                upholsteryGroup.push({ option: '2162', label: 'Paradise' })
                upholsteryGroup.push({ option: '2164', label: 'Carotene' })
                upholsteryGroup.push({ option: '2165', label: 'Molten' })
              }
              break
            case 'Poly Color':
              skinGroup.push({ option: 'BLU', label: 'Blue' })
              skinGroup.push({ option: 'BLK', label: 'Black' })
              skinGroup.push({ option: 'GRY', label: 'Gray' })
              break
            case 'ESD Vinyl':
              upholsteryGroup.push({ option: 'ESDBLU', label: 'Blue' })
              upholsteryGroup.push({ option: 'ESDBLK', label: 'Black' })
              break
            case 'ESD Fabric':
              upholsteryGroup.push({ option: 'ESDBLU', label: 'Blue' })
              upholsteryGroup.push({ option: 'ESDBLK', label: 'Black' })
              break
            case 'Shell Color':
              colorGroup.push({ option: 'BLK', label: 'Black' })
              colorGroup.push({ option: 'BLU', label: 'Blue' })
              colorGroup.push({ option: 'GRY', label: 'Gray' })
              break
            default:
              break
          }
        }
      }

      let tmpOptions = getSKU(id, optionsArg).replace(new RegExp(id), '').split('-').filter(opt => opt !== '')

      if (tiltGroup.length > 0) {
        const tiltOptions = tiltGroup.map((item: any) => item.option)
        const selectedTilt = optionsSelected.get(tiltOptions.reduce((a: string, b: string) => a + b))
        const selected = tmpOptions
          .find(opt => opt.includes('T'))
          ?.replace(new RegExp('T'), '')

        tmpOptionGroups.push({
          group: tiltGroup,
          selected: selected === undefined && selectedTilt == null ? 0 : selectedTilt != null ? tiltOptions.indexOf(selectedTilt!) : parseInt(selected!),
          type: 'basic',
          name: 'tilt',
        })
      }

      if (headRestGroup.length > 0) {
        const headRestOptions = headRestGroup.map((item: any) => item.option)
        const selectedHeadRest = optionsSelected.get(headRestOptions.reduce((a: string, b: string) => a + b))
        const selected = tmpOptions
          .find(opt => opt.includes('H'))
          ?.replace(new RegExp('H'), '')

        tmpOptionGroups.push({
          group: headRestGroup,
          selected: selected === undefined && selectedHeadRest == null ? 0 : selectedHeadRest != null ? headRestOptions.indexOf(selectedHeadRest!) : parseInt(selected!),
          type: 'basic',
          name: 'headrest',
        })
      }

      if (controlGroup.length > 0) {
        const controlOptions = controlGroup.map((item: any) => item.option)
        const selectedControl = optionsSelected.get(controlOptions.reduce((a: string, b: string) => a + b))
        const selected = parseInt(tmpOptions
          .find(opt => opt.includes('C'))
          ?.replace(new RegExp('C'), '') ?? "1") - 1

        tmpOptionGroups.push({
          group: controlGroup,
          selected: selected === undefined && selectedControl == null ? 0 : selectedControl != null ? controlOptions.indexOf(selectedControl!) : selected,
          type: 'basic',
          name: 'control',
        })
      }

      if (armGroup.length > 0) {
        const armOptions = armGroup.map(item => item.option)
        const selectedArm = optionsSelected.get(armOptions.reduce((a, b) => a + b))

        const selected = tmpOptions
          .find(opt => opt.includes('A'))
          ?.replace(new RegExp('A'), '')

        tmpOptionGroups.push({
          group: armGroup,
          selected: selected === undefined && selectedArm == null ? 0 : selectedArm != null ? armOptions.indexOf(selectedArm) : parseInt(selected ?? "0"),
          type: 'basic',
          name: 'arms',
        })
      }

      if (footringGroup.length > 0) {
        const footringOptions = footringGroup.map(item => item.option)
        var selectedFootring = optionsSelected.get(footringOptions.reduce((a, b) => a + b))

        if (selectedFootring == null) {
          footringOptions.forEach(footring => {
            if (tmpOptions.includes(footring) === true) {
              selectedFootring = footring
            }
          })
        }

        tmpOptionGroups.push({
          group: footringGroup,
          selected: selectedFootring === null
            ? 0 : footringOptions.indexOf(selectedFootring!),
          type: 'basic',
          name: 'footring',
        })
      }

      if (feetGroup.length > 0) {
        const feetOptions = feetGroup.map(item => item.option)
        var selectedOption = optionsSelected.get(feetOptions.reduce((a, b) => a + b))

        if (selectedOption == null) {
          feetOptions.forEach(feet => {
            if (tmpOptions.includes(feet) === true) {
              selectedOption = feet
            }
          })
        }

        tmpOptionGroups.push({
          group: feetGroup,
          selected: selectedOption === null
            ? 0
            : feetOptions.indexOf(selectedOption!),
          type: 'basic',
          name: 'feet',
        })
      }

      if (upholsteryGroup.length > 0) {
        const colors = upholsteryGroup.map(item => item.option)
        var selectedColor = optionsSelected.get(colors.reduce((a, b) => a + b))

        if (selectedColor == null) {
          colors.forEach(color => {
            if (tmpOptions.includes(color) === true) {
              selectedColor = color
            }
          })
        }

        tmpOptionGroups.push({
          group: upholsteryGroup,
          selected: selectedColor === null ? 0 : colors.indexOf(selectedColor!),
          type: 'image',
          name: 'upholstery',
        })
      }

      if (skinGroup.length > 0) {
        const colors = skinGroup.map(item => item.option)
        var selectedColor = optionsSelected.get(colors.reduce((a, b) => a + b))

        if (selectedColor == null) {
          colors.forEach(color => {
            if (tmpOptions.includes(color) === true) {
              selectedColor = color
            }
          })
        }

        tmpOptionGroups.push({
          group: skinGroup,
          selected: selectedColor === null ? 0 : colors.indexOf(selectedColor!),
          type: 'image',
          name: 'skin',
        })
      }

      if (colorGroup.length > 0) {
        const colors = colorGroup.map(item => item.option)
        var selectedColor = optionsSelected.get(colors.reduce((a, b) => a + b))

        if (selectedColor == null) {
          colors.forEach(color => {
            if (tmpOptions.includes(color) === true) {
              selectedColor = color
            }
          })
        }

        tmpOptionGroups.push({
          group: colorGroup,
          selected: selectedColor === null ? 0 : colors.indexOf(selectedColor!),
          type: 'image',
          name: 'color',
        })
      }

      for (var index = 0; index < tmpOptions.length; index += 1) {
        const option = tmpOptions[index]
        for (const optionGroup of optionGroups) {
          if (optionGroup.group.map((group: any) => group.option).includes(option)) {
            const tableKey = optionGroup.group.map((group: any) => group.option).reduce((a: string, b: string) => a + b)
            const lastSelected = optionsSelected.get(tableKey)

            if (lastSelected != null) {
              tmpOptions[index] = lastSelected
            }

          }
        }
      }

      return tmpOptionGroups

    } catch (error) {
      console.log(error)
      return null
    }
  }

  const calculateNodeDepth = (id: string) => {
    const node = treeData.find((node: Node) => node.id === id)
    let parent = node?.parent

    let depth = 0

    while (parent !== null && parent !== undefined) {
      depth += 1
      const node = treeData.find((node: Node) => node.id === parent)
      parent = node?.parent
    }

    return depth
  }

  const getInitialQuestions = () => {
    return treeData.filter((node: Node) => node.parent === rootID)
  }

  const getQuestions = () => {
    return path?.filter((node: Node) => node.type !== 'leaf')
  }

  const initPath = (data: Array<Node>, rootId: string, cache?: Array<string>): Array<Node> => {
    const secondaryNode = data.find((node: Node) => node.id === rootId)
    const path = [rootNode, secondaryNode]

    let children = data.filter((node: Node) => node.parent === rootId).sort((a, b) => a.index - b.index)
    while (children.length > 0) {
      const child = children.find(child => cache?.includes(child.name)) ?? children[0]
      path.push(child)
      children = data.filter((node: Node) => node.parent === child.id).sort((a, b) => a.index - b.index)
    }

    return path
  }

  const initSubPath = (data: Array<Node>, rootId: string, cache?: Array<string>): Array<Node> => {
    const root = treeData.find((node: Node) => node.id === rootId)
    const secondaryNode = data.find((node: Node) => node.id === root.parent)
    const path = [secondaryNode, root]

    let children = data.filter((node: Node) => node.parent === rootId).sort((a, b) => a.index - b.index)
    while (children.length > 0) {
      const child = children.find((child: Node) => cache?.includes(child.name)) ?? children[0]
      path.push(child)
      children = data.filter((node: Node) => node.parent === child.id).sort((a, b) => a.index - b.index)
    }

    return path
  }

  const onAnswerChange = (id: string) => {
    // If first question

    if (treeData.find((node: Node) => node.id === id)?.parent === rootID) {
      setSelectedRootAnswer(id)
    } else {
      setAnswerClicked(id)
    }
  }

  const onOptionChange = (groupIndex: number, option: string) => {
    const tmpOptions = [...(options ?? [])]
    tmpOptions[groupIndex] = option
    setOptions(tmpOptions)
    if (baseSKU) {
      setSKU(getSKU(baseSKU!, tmpOptions))
      onSetSKU(getSKU(baseSKU!, tmpOptions))
    }
  }

  const onSwatchChange = (key: number, groupName: string) => {
    const optionGroup = optionGroups.find(group => group.name === groupName)
    const groupIndex = optionGroups.findIndex(group => group.name === groupName)
    optionGroup.selected = key

    const optionCodes = optionGroup.group.map((group: any) => group.option)
    const tableKey = optionCodes.reduce((a: string, b: string) => a + b)
    const table = optionsSelected
    table.set(tableKey, optionCodes[optionGroup.selected])

    setOptionsSelected(table)
    onOptionChange(groupIndex, optionGroup.group[key].option)
  }

  const onImageSwatchChange = (key: number, groupName: string, offset: number) => {
    const optionGroup = optionGroups.find(group => group.name === groupName)
    const groupIndex = optionGroups.findIndex(group => group.name === groupName)
    const patriots = optionGroup.group.filter((node: Node) => node.option.includes('85') || node.option.includes('86'))
    const selected = key + (offset * (patriots.length))
    optionGroup.selected = selected

    const optionCodes = optionGroup.group.map((group: any) => group.option)
    const tableKey = optionCodes.reduce((a: string, b: string) => a + b)
    const table = optionsSelected
    table.set(tableKey, optionCodes[optionGroup.selected])
    setOptionsSelected(table)
    onOptionChange(groupIndex, optionGroup.group[selected].option)
  }

  const getPartnerProductURL = (partner: string): string => {
    switch (partner) {
      case 'labtech':
        return 'https://labtechseating.com/product/'
      default:
        return 'https://' + host + '/product?id='
    }
  }

  const onSetYLocations = (locations: Map<number, number>) => {
    const mergedLocations = new Map(yLocations)
    for (const [key, location] of locations.entries()) {
      mergedLocations.set(key, location)

    }
    setYLocations(mergedLocations)
  }

  const questions = getQuestions()

  return (
    <ConfiguratorV2
      sku={sku}
      customSKU={customSKU}
      displayName={displayName}
      baseSKU={baseSKU}
      catalog={catalog}
      partner={partner}
      accentColor={accentColor}
      color={color}
      productURL={getPartnerProductURL(partner)}
      description={description}
      optionGroups={optionGroups}
      treeData={treeData}
      path={path}
      initialQuestions={getInitialQuestions()}
      questions={selectedRootAnswer && questions?.length > 0 ? questions.slice(1, questions.length - 1) : []}
      selectedRootAnswer={selectedRootAnswer}
      setYLocations={onSetYLocations}
      onAnswerChange={onAnswerChange}
      showProductDisplay={showProductDisplay}
      onSwatchChange={onSwatchChange}
      onImageSwatchChange={onImageSwatchChange}
      onSecondaryHeightSwitchClicked={setSecondaryHeightSwitchClicked}
      onReset={onReset}
      openQuoteCart={openQuoteCart}
      extraAttributes={extraAttributes}
      initSKU={initSKU}
      setInitSKU={setInitSKU}
      setLeafNode={setLeafNode}
    />
  )
}

ConfiguratorV2CTN.propTypes = {
  customSKU: PropTypes.string,
  catalog: PropTypes.string.isRequired,
  scrollViewRef: PropTypes.object.isRequired,
  partner: PropTypes.string.isRequired,
  showProductDisplay: PropTypes.bool.isRequired,
  onSetSKU: PropTypes.func.isRequired,
  accentColor: PropTypes.string.isRequired,
  color: PropTypes.string,
  headerHeight: PropTypes.number,
  openQuoteCart: PropTypes.func.isRequired,
}
